import { isArray } from '@ember/array';
import { concat } from '@ember/helper';
import { hash } from '@ember/helper';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import type { WithBoundArgs } from '@glint/template';
import UiInput from 'garaje/components/ui-input';
import UiLabel from 'garaje/components/ui-label';
import UiTextarea from 'garaje/components/ui-textarea';
import { notEmpty } from 'macro-decorators';
import { localCopy } from 'tracked-toolbox';

interface UiInputFieldComponentSignature {
  Args: {
    autofocus?: boolean;
    dir?: string;
    disabled?: boolean;
    elementId?: string;
    errors?: string[] | string;
    isReadonly?: boolean;
    label?: string;
    max?: number | string;
    min?: number | string;
    step?: number | string;
    placeholder?: string;
    required?: boolean;
    type?: string;
    value?: string | number | null;
    onFocusIn?: (event: FocusEvent) => void;
    onFocusOut?: (event: FocusEvent) => void;
    update?: (value: string) => unknown;
  };
  Blocks: {
    default: [
      {
        label: WithBoundArgs<typeof UiLabel, 'errors' | 'for' | 'required' | 'text' | 'isReadonly'>;
        input: WithBoundArgs<
          typeof UiInput,
          | 'autofocus'
          | 'dir'
          | 'disabled'
          | 'focusIn'
          | 'focusOut'
          | 'hasError'
          | 'id'
          | 'isReadonly'
          | 'max'
          | 'min'
          | 'step'
          | 'placeholder'
          | 'type'
          | 'update'
          | 'value'
        >;
        textarea: WithBoundArgs<
          typeof UiTextarea,
          | 'autofocus'
          | 'dir'
          | 'disabled'
          | 'focusIn'
          | 'focusOut'
          | 'hasError'
          | 'id'
          | 'isReadonly'
          | 'placeholder'
          | 'update'
          | 'value'
        >;
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class UiInputField extends Component<UiInputFieldComponentSignature> {
  @localCopy('args.disabled', false) disabled!: boolean;
  @localCopy('args.required', false) required!: boolean;
  @localCopy('args.autofocus', false) autofocus!: boolean;
  @localCopy('args.errors', []) errors!: this['args']['errors'];
  @localCopy('args.type', 'text') type!: string;

  get elementId(): string {
    return this.args.elementId ?? this.#elementId;
  }

  #elementId = guidFor(this);

  @notEmpty('errors') hasErrors!: boolean;

  <template>
    <div ...attributes>
      {{#let
        (hash
          label=(component
            UiLabel
            errors=this.errors
            for=(concat this.elementId "-input")
            required=this.required
            text=@label
            isReadonly=@isReadonly
          )
          input=(component
            UiInput
            autofocus=this.autofocus
            dir=@dir
            disabled=this.disabled
            focusIn=@onFocusIn
            focusOut=@onFocusOut
            hasError=this.hasErrors
            id=(concat this.elementId "-input")
            isReadonly=@isReadonly
            max=@max
            min=@min
            step=@step
            placeholder=@placeholder
            type=this.type
            update=@update
            value=@value
          )
          textarea=(component
            UiTextarea
            autofocus=this.autofocus
            dir=@dir
            disabled=this.disabled
            focusIn=@onFocusIn
            focusOut=@onFocusOut
            hasError=this.hasErrors
            id=(concat this.elementId "-textarea")
            isReadonly=@isReadonly
            placeholder=@placeholder
            update=@update
            value=@value
          )
        )
        as |field|
      }}
        {{#if (has-block)}}
          {{yield field}}
        {{else}}
          {{field.label}}
          {{field.input}}
        {{/if}}
        {{#if this.hasErrors}}
          <div data-test-input-error class="bottom-error">
            {{#if (isArray this.errors)}}
              {{#each this.errors as |errorMessage|}}
                {{errorMessage}}
              {{/each}}
            {{else}}
              {{this.errors}}
            {{/if}}
          </div>
        {{/if}}
      {{/let}}
    </div>
  </template>
}
